import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, Users, Calendar, User, Send, ArrowLeft, RefreshCw, CheckCircle } from 'lucide-react';

interface User {
  id: number;
  name: string;
  email: string;
  phonenr: string;
}

interface Session {
  id: number;
  name: string;
  session_date: string;
  start_time: string;
  end_time: string;
  gusmester_name: string;
  participant_count: number;
}

interface SMSResponse {
  message: string;
  targetType: string;
  targetId?: number;
  sessionId?: number;
  messagePreview: string;
}

const AdminSMS: React.FC = () => {
  const [message, setMessage] = useState('');
  const [targetType, setTargetType] = useState<'all' | 'session' | 'single' | 'gusmasters'>('all');
  const [selectedSession, setSelectedSession] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSessions();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (targetType === 'session' && selectedSession) {
      fetchSessionUsers(selectedSession);
    } else if (targetType === 'single') {
      setUsers(allUsers);
    } else if (targetType === 'all') {
      setUsers(allUsers);
    } else if (targetType === 'gusmasters') {
      fetchUsers('gusmasters');
    }
  }, [targetType, selectedSession, allUsers]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get('/api/admin/sms/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError('Kunne ikke indlæse sessioner');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get('/api/admin/sms/users?type=all');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Kunne ikke indlæse brugere');
    }
  };

  const fetchUsers = async (type: string, sessionId?: number) => {
    try {
      let url = `/api/admin/sms/users?type=${type}`;
      if (sessionId) {
        url += `&sessionId=${sessionId}`;
      }
      const response = await axios.get(url);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Kunne ikke indlæse brugere');
    }
  };

  const fetchSessionUsers = async (sessionId: number) => {
    try {
      const response = await axios.get(`/api/admin/sms/users?type=session&sessionId=${sessionId}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching session users:', error);
      setError('Kunne ikke indlæse sessiondeltagere');
    }
  };

  const handleSendSMS = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setError('Beskeden må ikke være tom');
      return;
    }

    if (targetType === 'session' && !selectedSession) {
      setError('Vælg en session');
      return;
    }

    if (targetType === 'single' && !selectedUser) {
      setError('Vælg en bruger');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        message: message.trim(),
        targetType,
        targetId: targetType === 'single' ? selectedUser : undefined,
        sessionId: targetType === 'session' ? selectedSession : undefined
      };

      const response = await axios.post('/api/admin/sms/send', payload);
      const data: SMSResponse = response.data;
      
      setSuccess(`SMS sendt til ${getTargetDescription()}: "${data.messagePreview}"`);
      setMessage('');
      setSelectedSession(null);
      setSelectedUser(null);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fejl ved afsendelse af SMS');
    } finally {
      setSending(false);
    }
  };

  const getTargetDescription = () => {
    switch (targetType) {
      case 'all':
        return `alle medlemmer (${users.length} med telefonnummer)`;
      case 'session':
        const session = sessions.find(s => s.id === selectedSession);
        return `deltagere i ${session?.name || 'session'} (${users.length} med telefonnummer)`;
      case 'single':
        const user = users.find(u => u.id === selectedUser);
        return user?.name || 'valgt bruger';
      default:
        return 'ukendt mål';
    }
  };

  const getRecipientCount = () => {
    if (targetType === 'single') {
      return 1;
    }
    return users.length;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Link
              to="/admin"
              className="text-gray-600 hover:text-gray-900 mr-4 flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              Tilbage til Admin
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">SMS Beskeder</h1>
          <p className="text-gray-600 mt-2">Send SMS beskeder til medlemmer</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              {success}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SMS Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <MessageSquare className="w-6 h-6 mr-2 text-blue-700" />
              Send SMS Besked
            </h2>

            <form onSubmit={handleSendSMS} className="space-y-6">
              {/* Target Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Vælg modtagere
                </label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="all"
                      checked={targetType === 'all'}
                      onChange={(e) => setTargetType(e.target.value as 'all' | 'session' | 'single' | 'gusmasters')}
                      className="mr-3"
                    />
                    <Users className="w-5 h-5 mr-2 text-blue-600" />
                    <span>Alle medlemmer med telefonnummer</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="gusmasters"
                      checked={targetType === 'gusmasters'}
                      onChange={(e) => setTargetType(e.target.value as 'all' | 'session' | 'single' | 'gusmasters')}
                      className="mr-3"
                    />
                    <Users className="w-5 h-5 mr-2 text-blue-700" />
                    <span>Alle Gusmestre med telefonnummer</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="session"
                      checked={targetType === 'session'}
                      onChange={(e) => setTargetType(e.target.value as 'all' | 'session' | 'single' | 'gusmasters')}
                      className="mr-3"
                    />
                    <Calendar className="w-5 h-5 mr-2 text-green-600" />
                    <span>Deltagere i specifik session</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="single"
                      checked={targetType === 'single'}
                      onChange={(e) => setTargetType(e.target.value as 'all' | 'session' | 'single' | 'gusmasters')}
                      className="mr-3"
                    />
                    <User className="w-5 h-5 mr-2 text-purple-600" />
                    <span>Enkelt medlem</span>
                  </label>
                </div>
              </div>

              {/* Session Selection */}
              {targetType === 'session' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vælg session
                  </label>
                  <select
                    value={selectedSession || ''}
                    onChange={(e) => setSelectedSession(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Vælg en session...</option>
                    {sessions.map(session => (
                      <option key={session.id} value={session.id}>
                        {session.name} - {formatDate(session.session_date)} {formatTime(session.start_time)} ({session.participant_count} deltagere)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* User Selection */}
              {targetType === 'single' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vælg medlem
                  </label>
                  <select
                    value={selectedUser || ''}
                    onChange={(e) => setSelectedUser(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Vælg et medlem...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.phonenr})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Message Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Besked
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Indtast din SMS besked her..."
                  maxLength={160}
                />
                <div className="text-sm text-gray-500 mt-1">
                  {message.length}/160 tegn
                </div>
              </div>

              {/* Send Button */}
              <button
                type="submit"
                disabled={sending || !message.trim() || (targetType === 'session' && !selectedSession) || (targetType === 'single' && !selectedUser)}
                className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Sender...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send SMS til {getRecipientCount()} {getRecipientCount() === 1 ? 'modtager' : 'modtagere'}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Recipients Preview */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Users className="w-6 h-6 mr-2 text-blue-600" />
              Modtagere ({getRecipientCount()})
            </h2>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Indlæser...</span>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Ingen modtagere fundet</p>
                <p className="text-sm text-gray-400 mt-1">
                  {targetType === 'session' && !selectedSession && 'Vælg en session'}
                  {targetType === 'single' && !selectedUser && 'Vælg et medlem'}
                  {targetType === 'all' && 'Ingen medlemmer har telefonnummer'}
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {users.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                    <div className="text-sm font-mono text-gray-600">
                      {user.phonenr}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SMS Guidelines */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">SMS Retningslinjer</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• SMS beskeder er begrænset til 160 tegn</li>
            <li>• Kun medlemmer med registrerede telefonnumre modtager beskeder</li>
            <li>• SMS aktivitet logges i systemet</li>
            <li>• Vær respektfuld og relevant i dine beskeder</li>
            <li>• Test med enkelt medlem først hvis usikker</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSMS;
